/**
 * @file LabelStatusBadge.tsx
 * @description Статус этикеток партии: «Этикетки ✓ 24», «Этикетки устарели»,
 *   «Этикеток нет» (phase19.22, W17-9c; ui-design §1, §2).
 *
 * Один бейдж на строку приёмки, строку остатка и колонку «Этикетки»: иначе
 * «устарели» в приёмке и в остатках начинают писаться разными словами.
 * Тон и текст — реестром по статусу, а не тернарниками в разметке.
 *
 * ```tsx
 * <LabelStatusBadge status="PRINTED" count={24} />
 * <LabelStatusBadge status="STALE" />
 * <LabelStatusBadge status="PRINTED" printedAt="2026-09-25T14:02:00Z" variant="cell" />
 * ```
 */

import { Badge, type BadgeProps } from "../../ui/badge";
import type { LotLabelStatus } from "../../../lib/api-labels";

interface StatusMeta {
  /** Подпись в карточке (`card`) и в колонке таблицы (`cell`). */
  card: string;
  cell: string;
  variant: BadgeProps["variant"];
}

/** Реестр статусов: новый статус = запись здесь. */
const STATUS_META: Record<LotLabelStatus, StatusMeta> = {
  PRINTED: { card: "Этикетки ✓", cell: "✓", variant: "primary" },
  STALE: { card: "Этикетки устарели", cell: "устарели", variant: "warning" },
  NONE: { card: "Этикеток нет", cell: "нет", variant: "outline" },
};

interface Props {
  status: LotLabelStatus | null | undefined;
  /** Сколько этикеток напечатано — «✓ 24» (приёмка). */
  count?: number;
  /** Дата печати — «✓ 25.09» (остатки). */
  printedAt?: string | null;
  /** `card` — полная подпись, `cell` — короткая для колонки. */
  variant?: "card" | "cell";
}

/** «25.09» — день и месяц печати. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

export function LabelStatusBadge({ status, count, printedAt, variant = "card" }: Props) {
  // Партии нет (старая приёмка без технической партии) — этикетку не на что печатать.
  if (!status) return <span className="text-on-surface-variant">—</span>;
  const meta = STATUS_META[status];
  const base = variant === "card" ? meta.card : meta.cell;
  let tail = "";
  if (status === "PRINTED" && count !== undefined) tail = ` ${count}`;
  else if (status === "PRINTED" && printedAt) tail = ` ${shortDate(printedAt)}`;
  return (
    <Badge variant={meta.variant} className="whitespace-nowrap">
      {base}
      {tail}
    </Badge>
  );
}
